import { CoinsIcon } from 'lucide-react'
import { useState } from 'react'

import { PageBody, PageHeader } from '@/components/AppShell'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute, formatMicros } from '@/lib/format'

import { itemsOf, useStaffLedger, type LedgerFilters } from '../api'
import { dateToInstant } from '../filters'
import { LoadMore, StaffGate, StaffLink } from '../ui'
import { SELECT_CLASS } from '../shared'

const KINDS = ['TOPUP', 'CHARGE', 'REFUND', 'ADJUSTMENT', 'STAFF_CREDIT', 'STAFF_REFUND']

interface Draft {
  account_id: string
  kind: string
  from: string
  to: string
}

const EMPTY: Draft = { account_id: '', kind: '', from: '', to: '' }

/** Draft form values (a date input gives `YYYY-MM-DD`) to the query's ISO instants. */
function toFilters(d: Draft): LedgerFilters {
  const f: LedgerFilters = {}
  if (d.account_id.trim() !== '') f.account_id = d.account_id.trim()
  if (d.kind !== '') f.kind = d.kind
  const from = dateToInstant(d.from, false)
  const to = dateToInstant(d.to, true)
  if (from !== undefined) f.from = from
  if (to !== undefined) f.to = to
  return f
}

export function StaffLedgerPage() {
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [applied, setApplied] = useState<Draft>(EMPTY)
  const list = useStaffLedger(toFilters(applied))
  const rows = itemsOf(list.data)
  const set = (key: keyof Draft, value: string) => { setDraft((d) => ({ ...d, [key]: value })) }

  return (
    <StaffGate>
      <PageHeader eyebrow="Staff" title="Ledger" description="Balance entries across every account, newest first." />
      <PageBody>
        <Card>
          <CardContent className="space-y-4">
            <form
              className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
              onSubmit={(e) => {
                e.preventDefault()
                setApplied(draft)
              }}
            >
              <div className="min-w-0 space-y-1.5 lg:col-span-2">
                <Label htmlFor="led-acc">Account id</Label>
                <Input id="led-acc" className="font-mono" value={draft.account_id} onChange={(e) => { set('account_id', e.target.value) }} />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="led-kind">Kind</Label>
                <select id="led-kind" className={SELECT_CLASS} value={draft.kind} onChange={(e) => { set('kind', e.target.value) }}>
                  <option value="">Any</option>
                  {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="led-from">From</Label>
                <Input id="led-from" type="date" value={draft.from} onChange={(e) => { set('from', e.target.value) }} />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="led-to">To</Label>
                <Input id="led-to" type="date" value={draft.to} onChange={(e) => { set('to', e.target.value) }} />
              </div>
              <div className="flex items-end gap-2">
                <Button type="submit">Apply</Button>
                <Button type="button" variant="ghost" onClick={() => { setDraft(EMPTY); setApplied(EMPTY) }}>Clear</Button>
              </div>
            </form>

            {list.isError ? (
              <ErrorState error={list.error} title="Could not load the ledger" onRetry={() => void list.refetch()} />
            ) : list.isPending ? (
              <TableSkeleton rows={6} columns={6} />
            ) : rows.length === 0 ? (
              <EmptyState icon={CoinsIcon} title="No entries match" description="Nothing in the ledger fits these filters." />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead>
                        <TableHead>Account</TableHead>
                        <TableHead>Kind</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance after</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.id} data-testid="staff-ledger-row">
                          <TableCell className="text-xs text-muted-foreground tabular-nums">{formatAbsolute(r.created_at)}</TableCell>
                          <TableCell className="max-w-[14rem] truncate"><StaffLink to={`/app/staff/accounts/${r.account_id}`}>{r.account_name}</StaffLink></TableCell>
                          <TableCell><Badge variant="outline">{r.kind}</Badge></TableCell>
                          <TableCell className="text-right tabular-nums">{formatMicros(r.amount_micros, r.currency)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMicros(r.balance_after_micros, r.currency)}</TableCell>
                          <TableCell className="max-w-[20rem] truncate text-xs text-muted-foreground" title={r.description ?? undefined}>{r.description ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <LoadMore shown={rows.length} hasNext={list.hasNextPage} loading={list.isFetchingNextPage} onMore={() => void list.fetchNextPage()} />
              </>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </StaffGate>
  )
}
